<script setup lang="ts">
  import type { HTMLAttributes } from 'vue'
  import { computed, inject } from 'vue'
  import { cn } from '../../../lib/utils'
  import { insetInputResetClasses } from '../../form/form-internal'
  import { FIELD_CONTEXT_KEY } from './field-context'

  const props = defineProps<{
    class?: HTMLAttributes['class']
    /**
     * Lay this group's fields out horizontally on one row (e.g. Full Name +
     * Initials) instead of stacking them. Nest `<FieldGroup row>` inside a
     * stacked `<FieldGroup>`: the outer card keeps the border and corner
     * rounding and treats the row as a single stacked band (hairline divider
     * above it, square inner corners); the row itself strips its fields'
     * own borders and draws vertical hairline dividers between them.
     *
     * Sizing is the caller's job — pass `class="flex-1"` / `class="w-24"`
     * on each child `FormField`. The row is layout-only; it doesn't decide
     * how wide each field should be. Supports any number of children.
     */
    row?: boolean
  }>()

  const context = inject(FIELD_CONTEXT_KEY, { variant: 'default' as const })

  // A row participates in its parent stacked card as one band, so it borrows
  // the `input-group` slot the card's band rules already key on (border /
  // rounding reset, first/last corner rounding, the divider between bands).
  // A plain (non-row) group keeps the `field-group` slot.
  const dataSlot = computed(() => (props.row ? 'input-group' : 'field-group'))

  const rowClasses = [
    'flex min-w-0',
    // The row's fields are a level below the stacked card, out of reach of
    // the card's direct-child border reset, so strip each field's own
    // InputGroup chrome here.
    '[&>[data-slot=input-group]]:rounded-none [&>[data-slot=input-group]]:border-0 [&>[data-slot=input-group]]:bg-transparent [&>[data-slot=input-group]]:shadow-none',
    // Vertical hairline between adjacent fields — the column analogue of the
    // card's between-band divider. (`divide-x` loses to the border-0 reset
    // above, so match the card's explicit adjacency-border pattern instead.)
    '[&>[data-slot=input-group]+[data-slot=input-group]]:border-input [&>[data-slot=input-group]+[data-slot=input-group]]:border-l',
    // Round the outer fields so they follow the card's corners — but only
    // when this row is the card's first/last band: top corners when it's the
    // first child, bottom corners when it's the last, square otherwise. The
    // per-field focus ring inherits the radius for free.
    '[&:first-child>[data-slot=input-group]:first-child]:rounded-tl-md [&:first-child>[data-slot=input-group]:last-child]:rounded-tr-md',
    '[&:last-child>[data-slot=input-group]:first-child]:rounded-bl-md [&:last-child>[data-slot=input-group]:last-child]:rounded-br-md'
  ]

  const stackedClasses = [
    'bg-card border-input dark:bg-input/30 rounded-md border',
    '[&>[data-slot=input-group]]:rounded-none [&>[data-slot=input-group]]:border-0 [&>[data-slot=input-group]]:bg-transparent [&>[data-slot=input-group]]:shadow-none',
    '[&>[data-slot=input-group]:first-child]:rounded-t-md [&>[data-slot=input-group]:last-child]:rounded-b-md',
    '[&>[data-slot=input-group]+[data-slot=input-group]]:border-input [&>[data-slot=input-group]+[data-slot=input-group]]:border-t',
    insetInputResetClasses
  ]

  const defaultClasses =
    'group/field-group @container/field-group flex w-full flex-col gap-4'

  const classes = computed(() =>
    props.row
      ? rowClasses
      : context.variant === 'stacked'
        ? stackedClasses
        : defaultClasses
  )
</script>

<template>
  <div
    :data-slot="dataSlot"
    :role="row ? 'group' : undefined"
    :class="cn(classes, props.class)"
  >
    <slot />
  </div>
</template>
